import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { EnhancedSeoHead, FAQ } from "@appcaire/shared/seo";
import {
  generateArticleSchema,
  generateFAQSchema,
  generateBreadcrumbSchema,
  combineSchemas,
} from "@appcaire/shared/seo";
import {
  RefreshCw,
  Phone,
  FileText,
  CheckCircle2,
  AlertCircle,
  ArrowRight,
} from "lucide-react";

const STEPS = [
  {
    number: 1,
    title: "Kontakta biståndshandläggaren",
    icon: Phone,
    description:
      "Ring Nacka kommuns servicecenter (08-718 80 00) och säg att du vill byta hemtjänstutförare.",
  },
  {
    number: 2,
    title: "Välj ny anordnare",
    icon: CheckCircle2,
    description:
      "Du får hjälp att välja bland godkända utförare. Jämför gärna på vår sida för att hitta hemtjänst.",
  },
  {
    number: 3,
    title: "Kommunen sköter uppsägningen",
    icon: FileText,
    description:
      "Du behöver inte säga upp dig själv. Handläggaren meddelar din nuvarande utförare.",
  },
  {
    number: 4,
    title: "Möte med den nya utföraren",
    icon: RefreshCw,
    description:
      "Den nya anordnaren kontaktar dig, går igenom behoven och planerar starten.",
  },
];

const REASONS = [
  "Missnöje med kvalitet, tider eller bemötande",
  "Dålig personalkontinuitet – för många olika personer",
  "Kommunikationsproblem med utföraren",
  "Du vill prova något annat – du behöver inte ange skäl",
];

const BYTA_FAQ_ITEMS = [
  {
    question: "Hur byter jag hemtjänstutförare i Nacka?",
    answer:
      "Kontakta din biståndshandläggare via Nacka kommuns servicecenter på 08-718 80 00 och säg att du vill byta. Kommunen hjälper dig välja ny godkänd utförare och meddelar den nuvarande.",
  },
  {
    question: "Behöver jag ange anledning för att byta?",
    answer:
      "Nej. I Nacka har du kundval och kan byta när du vill utan att motivera dig. Det räcker att du meddelar handläggaren vilket nytt val du vill göra.",
  },
  {
    question: "Hur lång tid tar ett byte?",
    answer:
      "Oftast tar övergången några veckor beroende på schemaläggning. I akuta fall kan bytet gå snabbare. Fråga din handläggare om ungefärlig tidplan i ditt ärende.",
  },
];

/**
 * Byta - Guide to switching home care providers in Nacka
 *
 * SEO Focus: "byta hemtjänst Nacka", "byta hemtjänstutförare Nacka"
 */
const Byta: React.FC = () => {
  const articleSchema = useMemo(
    () =>
      generateArticleSchema({
        headline: "Byta hemtjänstutförare i Nacka | Så gör du",
        description:
          "Så byter du hemtjänstutförare i Nacka. Steg-för-steg: kontakta handläggaren, välj ny anordnare och låt kommunen sköta övergången.",
        url: "https://nackahemtjanst.se/byta",
        datePublished: "2026-07-25",
        dateModified: "2026-07-25",
      }),
    [],
  );

  const faqSchema = useMemo(
    () =>
      generateFAQSchema(
        BYTA_FAQ_ITEMS.map((item) => ({
          question: item.question,
          answer: item.answer,
        })),
        "https://nackahemtjanst.se/byta",
      ),
    [],
  );

  const breadcrumbSchema = useMemo(
    () =>
      generateBreadcrumbSchema([
        { name: "Hem", url: "https://nackahemtjanst.se/" },
        {
          name: "Byta hemtjänstutförare",
          url: "https://nackahemtjanst.se/byta",
        },
      ]),
    [],
  );

  const combinedSchema = useMemo(
    () => combineSchemas([articleSchema, faqSchema, breadcrumbSchema]),
    [articleSchema, faqSchema, breadcrumbSchema],
  );

  return (
    <>
      <EnhancedSeoHead
        title="Byta hemtjänstutförare i Nacka | Så gör du"
        description="Så byter du hemtjänstutförare i Nacka. Steg-för-steg: kontakta handläggaren, välj ny anordnare och låt kommunen sköta övergången."
        canonicalUrl="https://nackahemtjanst.se/byta"
        ogType="article"
        keywords={[
          "byta hemtjänst Nacka",
          "byta hemtjänstutförare Nacka",
          "byta anordnare Nacka",
          "kundval hemtjänst Nacka",
        ]}
        contentClassification="Consumer Guide"
        targetAudience="seniors, families"
        coverage="Sweden"
        publishDate="2026-07-25T00:00:00+02:00"
        modifiedDate="2026-07-25T00:00:00+02:00"
        author="Nacka Hemtjänst"
        schemaMarkup={combinedSchema ?? undefined}
        entities={[{ type: "Place", name: "Nacka" }]}
      />

      <section className="relative bg-gradient-to-b from-blue-50 via-white to-white py-16 md:py-24 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-100/20 via-transparent to-emerald-100/20" />
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <div className="bg-glass-white backdrop-blur-glass border border-glass-border rounded-2xl p-6 inline-block mb-6 shadow-glass-light">
            <RefreshCw className="w-12 h-12 text-blue-600" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-surface-900 tracking-tight mb-6">
            Byta hemtjänstutförare i Nacka
          </h1>
          <p className="text-xl text-surface-600 max-w-2xl mx-auto leading-relaxed">
            Du byter genom att kontakta din biståndshandläggare, välja en ny
            godkänd utförare och låta kommunen meddela den nuvarande. Du behöver
            inte ange skäl.
          </p>
        </div>
      </section>

      <section className="py-12 px-4 bg-white">
        <div className="max-w-4xl mx-auto bg-glass-white backdrop-blur-glass border border-glass-border rounded-2xl p-6 md:p-8 shadow-glass-light text-surface-700 leading-relaxed">
          <p>
            Osäker på vilken utförare som passar? Läs{" "}
            <Link
              to="/valja"
              className="text-emerald-600 font-medium hover:underline"
            >
              hur du väljer hemtjänst
            </Link>
            , jämför på{" "}
            <Link
              to="/hitta-hemtjanst"
              className="text-emerald-600 font-medium hover:underline"
            >
              hitta hemtjänst
            </Link>
            , eller se{" "}
            <Link
              to="/rattigheter"
              className="text-emerald-600 font-medium hover:underline"
            >
              dina rättigheter
            </Link>
            . Saknar du beslut? Börja med att{" "}
            <Link
              to="/ansoka"
              className="text-emerald-600 font-medium hover:underline"
            >
              ansöka
            </Link>
            .
          </p>
        </div>
      </section>

      <section className="py-16 px-4 bg-gradient-to-b from-white to-surface-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-surface-900 mb-10 text-center">
            Så går bytet till – 4 steg
          </h2>
          <div className="space-y-6">
            {STEPS.map((step) => (
              <div
                key={step.number}
                className="bg-glass-white backdrop-blur-glass border border-glass-border rounded-2xl p-6 shadow-glass-light flex gap-4"
              >
                <div className="w-12 h-12 rounded-xl bg-blue-600 text-white flex items-center justify-center font-bold flex-shrink-0">
                  {step.number}
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <step.icon className="w-5 h-5 text-blue-600" />
                    <h3 className="text-xl font-bold text-surface-900">
                      {step.title}
                    </h3>
                  </div>
                  <p className="text-surface-600">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 px-4 bg-white">
        <div className="max-w-4xl mx-auto grid md:grid-cols-2 gap-6">
          <div className="bg-glass-white backdrop-blur-glass border border-glass-border rounded-2xl p-6 shadow-glass-light">
            <h2 className="text-xl font-bold text-surface-900 mb-4">
              Vanliga anledningar att byta
            </h2>
            <ul className="space-y-3">
              {REASONS.map((reason) => (
                <li key={reason} className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                  <span className="text-surface-700">{reason}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-2xl p-6 flex gap-3">
            <AlertCircle className="w-6 h-6 text-blue-600 flex-shrink-0" />
            <div className="text-blue-900 space-y-2 text-sm">
              <p className="font-bold text-base">Viktigt att veta</p>
              <p>• Du behöver inte ange anledning</p>
              <p>• Kommunen meddelar den nuvarande utföraren</p>
              <p>• Du kan byta igen om det behövs</p>
              <p>• Avgiften styrs av kommunen, inte av utföraren</p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 px-4 bg-gradient-to-b from-surface-50 to-white">
        <div className="max-w-4xl mx-auto">
          <FAQ items={BYTA_FAQ_ITEMS} title="Vanliga frågor om att byta" />
        </div>
      </section>

      <section className="py-16 px-4 bg-white">
        <div className="max-w-4xl mx-auto text-center bg-glass-white backdrop-blur-glass border border-glass-border rounded-2xl p-8 shadow-glass-light">
          <h2 className="text-3xl font-bold text-surface-900 mb-6">
            Hitta en ny anordnare
          </h2>
          <p className="text-xl text-surface-600 mb-8 max-w-2xl mx-auto">
            Jämför godkända hemtjänstutförare i Nacka innan du meddelar ditt nya
            val.
          </p>
          <Link
            to="/hitta-hemtjanst"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-emerald-600 text-white text-lg font-semibold shadow-lg hover:bg-emerald-700 transition-all"
          >
            Hitta hemtjänst
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </>
  );
};

export default Byta;
